import type {
  BilletOrderResponseViewModel,
  IndividualPaymentParcelViewModel,
} from '@/shared/api/generated';

import { EPaymentType } from './parcelOperationAvailability';

/** Flags `int32` do backend usadas como booleano. */
const FLAG_ON = 1;
const FLAG_OFF = 0;

/**
 * Como o boleto da parcela é obtido.
 *
 * - `download`: o boleto já existe e não precisa passar pelo provedor — baixa o
 *   PDF direto;
 * - `register`: registra no PecegePay e acompanha até o provedor confirmar.
 */
export type BilletIssueRoute = 'download' | 'register';

/**
 * Qual caminho o botão "Gerar boleto" toma nesta parcela.
 *
 * Regra interna do `generateBillet` do legado, que recebia justamente estes
 * quatro campos (`PaymentType`, `HasBilletGenerated`, `MustRegister`,
 * `ForceBilletPecegePay`):
 *
 * ```
 * HasBilletGenerated == 1
 *   && ((PaymentType != 6 && ForceBilletPecegePay == 0) || MustRegister == 0)
 *   → download direto
 * senão → registro PecegePay com polling
 * ```
 *
 * Lida em prosa: **só baixa direto quando já existe boleto gerado** e ele não
 * depende do PecegePay — ou porque não é boleto PecegePay nem forçado a ele, ou
 * porque a parcela dispensa registro. Qualquer outra combinação passa pelo
 * provedor.
 *
 * Os três flags chegam como `int32`, não `boolean`: a comparação é com `1` e `0`
 * de propósito, e um valor ausente cai no caminho do registro — que é o lado
 * seguro, porque registrar um boleto que já estava registrado é idempotente no
 * provedor, enquanto baixar um PDF que não existe devolve 404.
 *
 * @param parcel - Linha da grid.
 * @returns O caminho a seguir.
 *
 * @example
 * ```ts
 * resolveBilletIssueRoute({ hasBilletGenerated: 1, paymentType: 1, forceBilletPecegePay: 0 });
 * // 'download'
 * ```
 */
export function resolveBilletIssueRoute(
  parcel: Pick<
    IndividualPaymentParcelViewModel,
    'paymentType' | 'hasBilletGenerated' | 'mustRegister' | 'forceBilletPecegePay'
  >,
): BilletIssueRoute {
  if (parcel.hasBilletGenerated !== FLAG_ON) return 'register';

  const isIndependentOfPecegePay =
    parcel.paymentType !== EPaymentType.BilletPecegePay && parcel.forceBilletPecegePay === FLAG_OFF;

  const skipsRegistration = parcel.mustRegister === FLAG_OFF;

  return isIndependentOfPecegePay || skipsRegistration ? 'download' : 'register';
}

/**
 * Desfecho do registro no PecegePay, lido do pedido.
 *
 * - `pending`: o provedor ainda não respondeu — continuar o polling;
 * - `registered`: há boleto com `registeredAt`, e `pdfUrl` traz o PDF quando o
 *   provedor devolveu link;
 * - `failed`: o provedor registrou erro no boleto.
 */
export type BilletRegistrationOutcome =
  | { readonly status: 'pending' }
  | { readonly status: 'registered'; readonly pdfUrl: string | null }
  | { readonly status: 'failed'; readonly message: string };

/**
 * Lê o desfecho do registro a partir do pedido devolvido pelo provedor.
 *
 * **Decide por dado, não por string de status.** `BilletOrderResponseViewModel.status`
 * e `BoletoResponseViewModel.status` são `string` livre no spec, sem enum nem
 * lista de valores — um polling que comparasse `status === 'registered'` pararia
 * de funcionar em silêncio no dia em que o provedor mudasse o texto. `registeredAt`
 * e `error` são carimbos objetivos: existir já é a resposta.
 *
 * O primeiro boleto do pedido é o que importa: o pedido é criado por parcela, e o
 * legado também tratava um por vez.
 *
 * @param order - Resposta de `GET /pecegepay/billet/{id}/order`; `undefined`
 *   enquanto a primeira leitura não voltou.
 * @returns O desfecho, ou `pending` quando ainda não há sinal.
 */
export function readBilletRegistration(
  order: BilletOrderResponseViewModel | undefined,
): BilletRegistrationOutcome {
  const billet = order?.boletos?.[0];
  if (billet === undefined) return { status: 'pending' };

  if (billet.error) return { status: 'failed', message: billet.error };

  if (billet.registeredAt) {
    // `pdf` é o link do documento; `defaultUrl` é a página do boleto no provedor,
    // e serve de recuo quando o PDF direto não vem.
    return { status: 'registered', pdfUrl: billet.urls?.pdf ?? billet.urls?.defaultUrl ?? null };
  }

  return { status: 'pending' };
}
